import ReactSelect from 'react-select';
import { cn } from '@/lib/utils';

interface Option {
  label: string;
  value: string;
}

/**
 * A `Select` you can type into. Radix's has no filter box and its typeahead
 * only jumps to a prefix, which is unusable once a list runs past a screen.
 * react-select is already a dependency (the v1 upload uses it), so this is the
 * same control wearing our tokens.
 */
export default function SearchSelect({
  value,
  options,
  onChange,
  placeholder,
  bare,
  invalid,
  'aria-label': ariaLabel,
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
  placeholder?: string;
  /** Borderless, for the review grid's inline cells. */
  bare?: boolean;
  /** Draws the control's box in the destructive colour. */
  invalid?: boolean;
  'aria-label'?: string;
}) {
  const opts = options.map((o) => ({ label: o, value: o }));
  return (
    <ReactSelect<Option>
      unstyled
      components={{ IndicatorSeparator: null }}
      aria-label={ariaLabel}
      value={opts.find((o) => o.value === value) ?? null}
      options={opts}
      onChange={(o) => onChange(o?.value ?? '')}
      placeholder={placeholder ?? 'Select…'}
      // The wizard's content column is `overflow-y-auto`; an inline menu clips
      // against it.
      menuPortalTarget={document.body}
      styles={{ menuPortal: (b) => ({ ...b, zIndex: 50 }) }}
      classNames={{
        // Branch on `bare` once: the focus ring and the invalid border are
        // both non-bare-only, so testing it per class multiplied the paths.
        control: ({ isFocused }) =>
          bare
            ? 'text-sm ring-offset-background text-xs font-medium'
            : cn(
                'text-sm ring-offset-background h-10 rounded-md border border-input bg-background px-3 py-2',
                isFocused && 'ring-2 ring-ring ring-offset-2',
                invalid && 'border-destructive'
              ),
        valueContainer: () => cn(bare && 'gap-0'),
        placeholder: () => 'text-muted-foreground',
        input: () => 'text-foreground',
        singleValue: () => 'text-foreground',
        indicatorsContainer: () => 'gap-1',
        dropdownIndicator: () =>
          cn(
            'opacity-50 [&>svg]:h-4 [&>svg]:w-4',
            bare && 'opacity-30 hover:opacity-100'
          ),
        menu: () =>
          'mt-1 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md',
        menuList: () => 'max-h-96 p-1',
        option: ({ isFocused, isSelected }) =>
          cn(
            'cursor-default rounded-sm px-2 py-1.5 text-sm',
            isFocused && 'bg-accent text-accent-foreground',
            isSelected && 'font-medium'
          ),
        noOptionsMessage: () => 'p-2 text-sm text-muted-foreground',
      }}
    />
  );
}
